import { redirectDocument } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { cmsApiIsomorphicFetchFile } from '#/modules/cms/files/api/cmsApiFetchFile';
import { cmsViewFileRoute } from '#/modules/cms/files/routing/cmsFiles.routes';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';

import { CmsViewFilePageLoadable } from './CmsViewFilePageLoadable';

export async function loader({
  request,
  context,
  params: { '*': filename },
}: HorizonAppRouteLoaderArgs<typeof cmsViewFileRoute>) {
  if (!HORIZON_ENABLED) {
    await CmsViewFilePageLoadable.load();
    return { filename };
  }

  const { download } = getAppRouteSearchParams({
    url: request.url,
    route: cmsViewFileRoute,
  });

  const [fileDetails] = await Promise.all([
    cmsApiIsomorphicFetchFile({ request, context })(
      filename,
      download === 'true',
    ).catch(() => null),
    CmsViewFilePageLoadable.load(),
  ]);

  if (!fileDetails) return idealistNotFound({ filename: null });

  return fileDetails.displayPdf
    ? { filename }
    : redirectDocument(fileDetails.url, { status: 302 /* HTTP_FOUND */ });
}

export default function CmsViewFileHorizonRoute({
  loaderData: { filename },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  if (!filename) return <NotFoundPageLoadable />;

  return (
    <RouteModule title={i18n('Documents')}>
      <CmsViewFilePageLoadable filename={filename} />
    </RouteModule>
  );
}
